import React from 'react';
import PropTypes from 'prop-types';
import { Panel } from 'primereact/panel';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'primereact/button';
import { Field, useFormikContext } from 'formik';
import {
  FormikChips,
  LegacyInputTextComponent,
  FormikCheckbox,
  TextAreaWithValidation,
  FormikMultiSelect,
} from '../../../components/prime-formik';
import ArrayUtil from '../../../utils/ArrayUtil';
import { Loading } from '../../../components/Loading';
import { renderAsyncSelector } from '../../../components/formik-components/FormAsyncSelector';
import FormLanguageSelector from '../../../components/formik-components/FormLanguageSelector';
import { validateEmails } from '../../../i18n/common';
import SuperLinkActivationOptionsForm from './SuperLinkActivationOptionsForm';
import SuperLinkFormMinimumMatchPercentageInputGroup from './SuperLinkFormMinimumMatchPercentageInputGroup';

const i18n = defineMessages({
  EmailPlaceholder: {
    id: 'placeholder.email',
    defaultMessage: 'Email Address',
  },
  choose: {
    id: 'choose',
    defaultMessage: 'Choose',
  },
  sendIndividualReport: {
    id: 'superlink.send_individual_report_to_respondent',
    defaultMessage: 'Send Individual Report to respondent if distortion is acceptable',
  },
  sendInterestsCC: {
    id: 'superlink.send_cc-interests_report_to_respondent',
    defaultMessage: 'Send Career Counselling - Interests Report to respondent',
  },
  sendPersonalityCC: {
    id: 'superlink.send_cc-personality_report_to_respondent',
    defaultMessage: 'Send Career Counselling - Personality Report to respondent',
  },
  printerFriendly: {
    id: 'superlink.lbl_printer_friendly_report',
    defaultMessage: 'Printer friendly report(s)',
  },
});

const SuperLinkEditForm = ({
  handleCancel,
  superLink,
  listJobProfiles,
  searchJob,
  searching,
  reportForDropDown,
  locale,
  accountCanSelectMatchPercentageForCareerCounselingReports,
}) => {
  const { formatMessage } = useIntl();
  const {
    isSubmitting,
    setFieldValue,
    values: { activationType },
  } = useFormikContext();

  const sendIndividualReport = formatMessage(i18n.sendIndividualReport);
  const sendCCPersonalityLabel = formatMessage(i18n.sendPersonalityCC);
  const sendCCInterestsLabel = formatMessage(i18n.sendInterestsCC);
  const printerFriendly = formatMessage(i18n.printerFriendly);

  return (
    <Panel>
      <div className="p-grid">
        <div className="p-sm-12 p-md-6" />
        <div className="p-md-6" />
        <div className="p-sm-12 p-md-6" key="selections">
          <div className="p-grid p-fluid">
            <div className="p-col-12">
              <FormattedMessage id="attrs.name" defaultMessage="Name">
                {(text) => (
                  <Field
                    id="superlink-name"
                    name="superlinkName"
                    component={LegacyInputTextComponent}
                    placeholder={text}
                  />
                )}
              </FormattedMessage>
              <p>
                <FormattedMessage id="attrs.assessmentTemplate" defaultMessage="Assessment Template" />:{' '}
                <strong>{superLink.assessmentTemplate.title}</strong>
              </p>
            </div>
          </div>
          <div className="p-grid">
            <div className="p-col-12">
              {ArrayUtil.isNotEmpty(listJobProfiles) ? (
                <div className="form-group">
                  <label className="field-label" htmlFor="superlink-jobProfileId">
                    <FormattedMessage id="attrs.targetJob" defaultMessage="Target Job Profile" />
                  </label>
                  <Field
                    id="job-profile-id"
                    name="targetJobId"
                    component={renderAsyncSelector}
                    processing={searching}
                    data={listJobProfiles}
                    filter
                    valueField="id"
                    textField="name"
                    onSearch={searchJob}
                    setFieldValue={setFieldValue}
                    placeholder={`- ${formatMessage(i18n.choose)} -`}
                  />
                </div>
              ) : (
                <Loading />
              )}
            </div>
          </div>
          <SuperLinkActivationOptionsForm activationType={activationType} setFieldValue={setFieldValue} />
          {accountCanSelectMatchPercentageForCareerCounselingReports && (
            <SuperLinkFormMinimumMatchPercentageInputGroup />
          )}
          <div className="p-grid">
            <div className="p-col-12">
              <Field
                id="sendRespondentReport"
                name="sendRespondentReport"
                component={FormikCheckbox}
                labelText={sendIndividualReport}
                setFieldValue={setFieldValue}
              />
            </div>
          </div>
          <div className="p-grid">
            <div className="p-col-12">
              <Field
                id="sendRespondentCcInterestsReport"
                name="sendRespondentCcInterestsReport"
                component={FormikCheckbox}
                labelText={sendCCInterestsLabel}
                setFieldValue={setFieldValue}
              />
            </div>
          </div>
          <div className="p-grid">
            <div className="p-col-12">
              <Field
                id="sendRespondentCcPersonalityReport"
                name="sendRespondentCcPersonalityReport"
                component={FormikCheckbox}
                labelText={sendCCPersonalityLabel}
                setFieldValue={setFieldValue}
              />
            </div>
          </div>
          <div className="p-grid">
            <div className="p-col-12">
              <Field
                id="printFriendlyReport"
                name="printFriendlyReport"
                component={FormikCheckbox}
                labelText={printerFriendly}
                setFieldValue={setFieldValue}
              />
            </div>
          </div>
          <br />
          <div className="p-grid p-fluid">
            <div className="p-col-12">
              <label className="field-label" htmlFor="notificationEmails">
                <FormattedMessage id="superlink.notificationEmail" defaultMessage="Send auto-generated report(s) to" />
                <br />
                <FormattedMessage
                  id="superlink.emails.hint"
                  defaultMessage="Press ENTER after each provided email"
                  tagName="small"
                />
              </label>
              <Field
                id="notificationEmails"
                name="notificationEmails"
                component={FormikChips}
                setFieldValue={setFieldValue}
                validate={(e) => validateEmails(e, formatMessage)}
                addOnBlur
              />
            </div>
          </div>
          <div className="p-grid p-fluid">
            <div className="p-col-12">
              <label className="field-label" htmlFor="locale">
                <FormattedMessage id="superlink.reportLanguage" defaultMessage="Language" />
              </label>
              <Field id="locale" name="locale" component={FormLanguageSelector} options={locale} />
            </div>
          </div>
          <div className="p-grid">
            <div className="p-col-12">
              <FormattedMessage id="superlink.autogenerateReports" defaultMessage="AUTO-GENERATE REPORTS">
                {(text) => (
                  <strong>
                    <h3 className="field-label" htmlFor="reports">
                      {text}
                    </h3>
                  </strong>
                )}
              </FormattedMessage>
              <Field
                name="reports"
                options={reportForDropDown}
                setFieldValue={setFieldValue}
                component={FormikMultiSelect}
                placeholder="..."
                style={{ width: '100%' }}
              />
            </div>
          </div>
        </div>
        <div className="p-sm-12 p-md-6" key="info">
          <div className="p-grid p-fluid">
            <div className="p-col-12">
              <label className="field-label" htmlFor="description">
                <FormattedMessage id="superlink.description" defaultMessage="DESCRIPTION" />
              </label>
              <Field
                className="form-control"
                id="description"
                name="description"
                component={TextAreaWithValidation}
                rows={5}
              />
            </div>
          </div>
          <div className="p-grid p-fluid">
            <div className="p-col-12">
              <label className="field-label" htmlFor="notes">
                <FormattedMessage id="attrs.notes" defaultMessage="Notes" />
              </label>
              <Field className="form-control" id="notes" name="notes" component={TextAreaWithValidation} rows={5} />
            </div>
          </div>
        </div>
        <div className="p-sm-12">
          <FormattedMessage id="actions.save" defaultMessage="Save">
            {(text) => (
              <Button
                type="submit"
                icon={isSubmitting ? 'pi pi-spin pi-spinner' : ''}
                disabled={isSubmitting}
                label={text}
              />
            )}
          </FormattedMessage>
          &nbsp;
          <FormattedMessage id="actions.cancel" defaultMessage="Cancel">
            {(text) => (
              <Button
                type="button"
                className="p-button-secondary"
                disabled={isSubmitting}
                onClick={handleCancel}
                label={text}
              />
            )}
          </FormattedMessage>
        </div>
      </div>
    </Panel>
  );
};

SuperLinkEditForm.propTypes = {
  isSubmitting: PropTypes.bool.isRequired,
  searching: PropTypes.bool.isRequired,
  handleCancel: PropTypes.func.isRequired,
  searchJob: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  superLink: PropTypes.objectOf(PropTypes.any).isRequired,
  listJobProfiles: PropTypes.arrayOf(PropTypes.any).isRequired,
  reportForDropDown: PropTypes.arrayOf(PropTypes.any).isRequired,
  locale: PropTypes.arrayOf(PropTypes.any).isRequired,
  values: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default SuperLinkEditForm;
